import { useGetInvoices } from "@entities/accounting/lib/hooks/useGetInvoices";
import { useGetPurchases } from "@entities/accounting/lib/hooks/useGetPurchases";
import { IGetPurchase } from "@entities/accounting/lib/types";
import { useGetBrands } from "@entities/brand";
import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";
import { AccountingDateRange } from "@features/FinanceInvoiceFilters/types";
import { MonthDateFilter } from "@features/FinanceInvoiceFilters/ui/MonthDateFilter";
import { Table } from "@shared/ui";
import { SelectButton } from "@shared/ui/Select";
import { Button, Flex, Input, Space, Spin, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  Briefcase,
  Download,
  Globe,
  Plus,
  Receipt,
  RefreshCcw,
  Search,
  ShoppingBag,
  User,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AddPurchaseModal } from "../../../entities/accounting/ui/AddPurchaseModal";
import styles from "./styles.module.scss";

export function PurchasesTab() {
  const [isAddPurchaseOpen, setIsAddPurchaseOpen] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  // Filters State

  const [searchQuery, setSearchQuery] = useState("");

  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);

  const [brandSearch, setBrandSearch] = useState("");

  const [selectedBriefIds, setSelectedBriefIds] = useState<string[]>([]);

  const [briefSearch, setBriefSearch] = useState("");

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);

  const [invoiceSearch, setInvoiceSearch] = useState("");

  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const [dateFilter, setDateFilter] = useState<
    AccountingDateRange | undefined
  >();

  // Data Fetching
  const {
    purchases,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetPurchases({
    searchQuery: searchQuery || undefined,
    brandIds: selectedBrandIds,
    briefIds: selectedBriefIds,
    accountingIds: selectedInvoiceIds,
    category: categoryFilter,
    type: typeFilter,
    accountingFillingStatus: statusFilter,
    createdAtFrom: dateFilter?.from?.toISOString(),
    createdAtTo: dateFilter?.to?.toISOString(),
  });

  const { brands, isFetching: areBrandsLoading } = useGetBrands(
    10,
    1,
    brandSearch
  );
  const { briefs, isFetching: areBriefsLoading } = useGetBriefs({
    pageNo: 1,
    limit: 10,
    searchQuery: briefSearch ? briefSearch : undefined,
  });
  const { invoices, isFetching: areInvoicesLoading } = useGetInvoices({
    brandIds: selectedBrandIds?.length > 0 ? selectedBrandIds : undefined,
    searchQuery: invoiceSearch,
    pageNo: 1,
    limit: 10,
  });

  const brandOptions = useMemo(
    () => brands?.data?.map((b) => ({ label: b.name, value: b._id })) ?? [],
    [brands]
  );
  const briefOptions = useMemo(
    () => briefs?.map((b) => ({ label: b.name, value: b._id })) ?? [],
    [briefs]
  );
  const invoiceOptions = useMemo(
    () =>
      invoices?.map((i) => ({
        label: i.accountingNumber || i._id,
        value: i._id,
      })) ?? [],
    [invoices]
  );

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Creator":
        return <User size={14} />;
      case "Reimbursement":
        return <Receipt size={14} />;
      case "General":
        return <Briefcase size={14} />;
      case "Refund/Credit":
        return <RefreshCcw size={14} />;
      default:
        return <ShoppingBag size={14} />;
    }
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case "Creator":
        return "blue";
      case "Reimbursement":
        return "orange";
      case "General":
        return "gray";
      case "Refund/Credit":
        return "green";
      default:
        return "gray";
    }
  };

  // Infinite scroll: call fetchNextPage when table wrapper scrolls near bottom
  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const onScroll = () => {
      if (!hasNextPage || isFetchingNextPage) return;
      const threshold = 200; // px from bottom
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        fetchNextPage();
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, tableWrapperRef]);

  const columns: ColumnsType<IGetPurchase> = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date: string) =>
        new Date(date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      title: "Description",
      dataIndex: "remarks",
      key: "remarks",
      width: 150,
      render: (text, record) => (
        <div>
          <Typography.Text strong>{text}</Typography.Text>
          {record.brief && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: "11px", display: "block" }}
            >
              {record.brief.name}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
      render: (category) =>
        category ? (
          <Tag color={getCategoryColor(category)}>
            <Flex gap={5} align="center">
              {getCategoryIcon(category)} {category}
            </Flex>
          </Tag>
        ) : null,
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      width: 100,
      render: (brand) => (
        <Space>
          <Globe size={12} />
          {brand.name}
        </Space>
      ),
    },
    {
      title: "Reference",
      dataIndex: "billingId",
      key: "billingId",
      width: 200,
    },
    {
      title: "Base Cost",
      dataIndex: "sellingCost",
      key: "sellingCost",
      align: "right",
      width: 200,
      render: (cost, record) => (
        <span
          style={{ color: record.type === "Credit" ? "red" : "" }}
        >{`${record.type === "Credit" ? "-" : ""}₹${Math.abs(cost).toLocaleString()}`}</span>
      ),
    },
    {
      title: "Service Cost",
      dataIndex: "serviceCost",
      key: "serviceCost",
      align: "right",
      width: 200,
      render: (cost, record) => (
        <span
          style={{ color: record.type === "Credit" ? "red" : "" }}
        >{`${record.type === "Credit" ? "-" : ""}₹${Math.abs(cost).toLocaleString()}`}</span>
      ),
    },
    {
      title: "GST Cost",
      dataIndex: "gstAmount",
      key: "gstAmount",
      align: "right",
      width: 200,
      render: (cost, record) => (
        <span
          style={{ color: record.type === "Credit" ? "red" : "" }}
        >{`${record.type === "Credit" ? "-" : ""}₹${Math.abs(cost).toLocaleString()}`}</span>
      ),
    },
    {
      title: "Total Cost",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 250,
      render: (cost, record) => (
        <b
          style={{ color: record.type === "Credit" ? "red" : "" }}
        >{`${record.type === "Credit" ? "-" : ""}₹${Math.abs(cost).toLocaleString()}`}</b>
      ),
    },
    {
      title: "Status",
      dataIndex: "accountingFillingStatus",
      key: "accountingFillingStatus",
      align: "right",
      width: 150,
      render: (status) => {
        if (status === "Billed") {
          return (
            <Space direction="vertical" align="end" size={4}>
              <Tag color="green">Billed</Tag>
            </Space>
          );
        }
        if (status === "Settled") {
          return <Tag color="purple">Settled</Tag>;
        }
        return <Tag color="orange">Unbilled</Tag>;
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div ref={tableWrapperRef} style={{ maxHeight: 520, overflow: "auto" }}>
        <Table
          dataSource={purchases}
          columns={columns}
          rowKey="_id"
          pagination={false}
          // className={styles.table}
          title={() => <div>
        {/* Search Row */}
        <div className={styles.searchRow}>
          <Input
            prefix={<Search size={16} />}
            placeholder="Search by description, reference, or invoice..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{width: 300}}
          />
          <div className={styles.actions}>
            <Button
              icon={<Plus size={16} />}
              type="primary"
              onClick={() => setIsAddPurchaseOpen(true)}
            >
              Add Purchase
            </Button>
            <Button icon={<Download size={16} />}>Export CSV</Button>
          </div>
        </div>

        {/* Filters Row */}
        <Flex gap="small" wrap="wrap" className={styles.filters}>
          <SelectButton
            label="All Brands"
            options={brandOptions}
            value={selectedBrandIds}
            onChange={setSelectedBrandIds}
            mode="multiple"
            isLoading={areBrandsLoading}
            className={styles.select}
            searchable
            searchValue={brandSearch}
            onSearchChange={setBrandSearch}
            withFooterActions
          />
          <MonthDateFilter
            value={dateFilter}
            placeholder="All Months"
            onChange={setDateFilter}
          />
          <SelectButton
            label="All Campaigns"
            options={briefOptions}
            value={selectedBriefIds}
            onChange={setSelectedBriefIds}
            mode="multiple"
            isLoading={areBriefsLoading}
            disabled={selectedBrandIds?.length === 0 && !briefSearch}
            className={styles.select}
            searchable
            searchValue={briefSearch}
            onSearchChange={setBriefSearch}
            withFooterActions
          />
          <SelectButton
            label="All Categories"
            options={[
              { value: "Creator", label: "Creator" },
              { value: "Reimbursement", label: "Reimbursement" },
              { value: "General", label: "General" },
              { value: "Refund/Credit", label: "Refunds/Credits" },
            ]}
            value={categoryFilter}
            onChange={setCategoryFilter}
            mode="multiple"
            className={styles.select}
            withFooterActions
            // searchable
          />
          <SelectButton
            label="All Types"
            options={[
              { value: "Debit", label: "Debit" },
              { value: "Credit", label: "Credit" },
            ]}
            value={typeFilter}
            onChange={setTypeFilter}
            mode="multiple"
            className={styles.select}
            withFooterActions
            // searchable
          />
          <SelectButton
            label="All Statuses"
            options={[
              { value: "Billed", label: "Billed" },
              { value: "Unbilled", label: "Unbilled" },
              { value: "Settled", label: "Settled" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            mode="multiple"
            className={styles.select}
            withFooterActions
            // searchable
          />
          <SelectButton
            label="All Invoices"
            options={invoiceOptions}
            value={selectedInvoiceIds}
            onChange={setSelectedInvoiceIds}
            mode="multiple"
            isLoading={areInvoicesLoading}
            disabled={selectedBrandIds?.length === 0 && !invoiceSearch}
            className={styles.select}
            searchable
            searchValue={invoiceSearch}
            onSearchChange={setInvoiceSearch}
            withFooterActions
          />
        </Flex>
      </div>}
          loading={isFetching && !isFetchingNextPage}
          // summary={() => (
          //   <Table.Summary.Row className={styles.summaryRow}>
          //     <Table.Summary.Cell index={0} colSpan={5} align="right">
          //       <Typography.Text strong>Total</Typography.Text>
          //     </Table.Summary.Cell>
          //     <Table.Summary.Cell index={1} align="right">
          //       <Typography.Text strong>
          //         {/* ₹{totalBase.toLocaleString()} */}
          //       </Typography.Text>
          //     </Table.Summary.Cell>
          //     <Table.Summary.Cell index={2} align="right">
          //       <Typography.Text strong>
          //         {/* ₹{totalService.toLocaleString()} */}
          //       </Typography.Text>
          //     </Table.Summary.Cell>
          //     <Table.Summary.Cell index={3} align="right">
          //       <Typography.Text strong>
          //         {/* ₹{totalAmount.toLocaleString()} */}
          //       </Typography.Text>
          //     </Table.Summary.Cell>
          //     <Table.Summary.Cell index={4}></Table.Summary.Cell>
          //   </Table.Summary.Row>
          // )}
          scroll={{
            x: 1200,
          }}
        />

        {/* Bottom loader for infinite fetches */}
        {isFetchingNextPage && (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 12 }}
          >
            <Spin />
          </div>
        )}
      </div>
      {isAddPurchaseOpen ? (
        <AddPurchaseModal
          isOpen={isAddPurchaseOpen}
          onClose={() => setIsAddPurchaseOpen(false)}
        />
      ) : null}
    </div>
  );
}
